import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../App.css';
import shipraLogo from '../images/shipra-logo.png';
import { buildTrackingShareUrl } from '../../services/tracking-api';
import {
  buildGeocodeQueries,
  buildGeocodeRequest,
  formatCityStateCountry,
  placeFromAddressComponents,
} from '../../services/shipment-map-geocode';

const ADMIN_ACCESS_CODE = process.env.REACT_APP_ADMIN_ACCESS_CODE || '';

const hasAdminAccess = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('code') === ADMIN_ACCESS_CODE;
};

const FlowIcon = ({ name, size = 22, className = '' }) => {
  const icons = {
    globe: <><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    clipboard: <><rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4V2h6v2M8 9h8M8 13h8M8 17h5"/></>,
    box: <><path d="m4 8 8-4 8 4-8 4zM4 8v9l8 4 8-4V8M12 12v9"/><path d="m8 6 8 4"/></>,
    truck: <><path d="M3 7h10v9H3zM13 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></>,
    van: <><path className="van-body" d="M2.5 7h11v9h-11z"/><path className="van-cab" d="M13.5 10h4l3 3v3h-7z"/><path className="van-window" d="M15 11.5h2.2l1.5 1.7H15z"/><path d="M2 16h19"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></>,
    route: <><circle cx="7" cy="7" r="2"/><circle cx="17" cy="17" r="2"/><path d="M7 9v3c0 1 1 2 2 2h6M17 15V9"/></>,
    home: <><path d="m3 11 9-8 9 8M5 10v10h14V10M9 20v-6h6v6"/></>,
    user: <><circle cx="12" cy="7" r="4"/><path d="M4 21c0-5 3-8 8-8s8 3 8 8"/></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m4 7 8 6 8-6"/></>,
    phone: <path d="M6 3 3 5c0 8 8 16 16 16l2-3-5-3-2 2c-3-1-6-4-7-7l2-2z"/>,
    pin: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/></>,
    location: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" fill="currentColor"/><circle cx="12" cy="10" r="2.5" fill="#fff" stroke="none"/></>,
    copy: <><rect x="8" y="8" width="11" height="13" rx="2"/><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h3"/></>,
    chat: <path d="M4 4h16v12H9l-5 4z"/>,
    whatsapp: <><path fill="currentColor" stroke="none" d="M12 2a9.5 9.5 0 0 0-8.2 14.3L2.5 21.5l5.3-1.4A9.5 9.5 0 1 0 12 2Z"/><path fill="none" stroke="#fff" strokeWidth="1.7" d="M8.1 7.4c.5 4.1 4.4 8 8.5 8.5l1.2-1.8-3-1.5-1 1.2a7 7 0 0 1-3.6-3.6l1.2-1-1.5-3Z"/></>,
    admin: <><circle cx="12" cy="8" r="4"/><path d="M5 21c0-4 2.8-7 7-7s7 3 7 7"/><path d="M17 12.5 19 14l2-1.5v4l-2 1.5-2-1.5z"/></>,
  };

  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {icons[name] || icons.route}
    </svg>
  );
};

const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const hasValue = (value) => value !== null && value !== undefined && value !== '';
const maskEmail = (value) => {
  if (!hasValue(value)) return 'Not available';

  const email = String(value).trim();
  const [localPart, domainPart] = email.split('@');

  if (!localPart || !domainPart) {
    return '********';
  }

  const maskedLocal = localPart.length <= 2
    ? `${localPart.charAt(0)}*`
    : `${localPart.charAt(0)}${'*'.repeat(localPart.length - 2)}${localPart.slice(-1)}`;

  const domainParts = domainPart.split('.');
  const domainName = domainParts[0] || '';
  const domainExtension = domainParts.slice(1).join('.');

  const maskedDomain = domainName.length <= 1
    ? '*'
    : `${domainName.charAt(0)}${'*'.repeat(domainName.length - 1)}`;

  return `${maskedLocal}@${maskedDomain}${domainExtension ? `.${domainExtension}` : ''
    }`;
};

const maskPhone = (value) => {
  if (!hasValue(value)) return 'Not available';

  const phone = String(value).trim();
  const digits = phone.replace(/\D/g, '');

  if (digits.length <= 5) {
    return '*'.repeat(digits.length);
  }

  return `${digits.slice(0, 3)}${'*'.repeat(digits.length - 5)}${digits.slice(-2)}`;
};

const addressWords = (value) => String(value || '')
  .toLowerCase()
  .match(/[a-z0-9]+/g) || [];

const maskAddressLine = (value, visibleTerms = []) => {
  if (!hasValue(value)) return null;

  const address = String(value).trim();
  const parts = address.match(/\S+|\s+/g) || [];
  const words = parts.filter((part) => !/\s+/.test(part));
  const visibleWords = new Set([
    ...visibleTerms.flatMap(addressWords),
  ]);

  if (words.length === 1 && words[0].length > 3) {
    return `${words[0].slice(0, 3)}${'*'.repeat(words[0].length - 3)}`;
  }

  let wordIndex = 0;

  return parts.map((part) => {
    if (/\s+/.test(part)) return part;

    const normalizedWord = part.toLowerCase().replace(/[^a-z0-9&]/g, '');
    const shouldMask = wordIndex >= 3 && !visibleWords.has(normalizedWord);
    wordIndex += 1;

    return shouldMask ? '*'.repeat(part.length) : part;
  }).join('');
};

const hasRecordValues = (value) => isRecord(value) && Object.values(value).some(hasValue);

const formatDate = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatLocation = (value) => {
  if (typeof value === 'string') return value.trim() || null;
  if (!hasRecordValues(value)) return null;

  const directLabel = value.label || value.pickup_address || value.address || value.address1 || value.name || value.formattedAddress || value.formatted_address;
  if (typeof directLabel === 'string' && directLabel.trim()) return directLabel.trim();

  const city = value.city || value.district;
  const state = value.state || value.province || value.provinceCode;
  const country = value.country || value.countryCode || value.countryCodeV2;
  const parts = [city, state, country].filter(hasValue).map(String);
  if (parts.length) return parts.join(', ');

  return null;
};

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
let mapsLoader;

const loadGoogleMaps = () => {
  if (window.google?.maps?.Geocoder) return Promise.resolve(window.google.maps);
  if (mapsLoader) return mapsLoader;
  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error('Google Maps is not configured. Restart npm start after setting REACT_APP_GOOGLE_MAPS_API_KEY in .env.local.'));
  }

  mapsLoader = new Promise((resolve, reject) => {
    const fail = (message) => {
      mapsLoader = undefined;
      reject(new Error(message));
    };

    window.gm_authFailure = () => {
      fail('Google Maps authentication failed. Confirm billing is enabled and this site is allowed on the API key.');
    };

    const finish = async () => {
      try {
        if (window.google?.maps?.importLibrary) {
          await window.google.maps.importLibrary('maps');
          await window.google.maps.importLibrary('geocoding');
          try {
            await window.google.maps.importLibrary('routes');
          } catch {
            // Directions is optional; a straight orange line is used as fallback.
          }
        }
        if (!window.google?.maps?.Geocoder) {
          fail('Google Geocoder is unavailable. Enable the Geocoding API and reload.');
          return;
        }
        resolve(window.google.maps);
      } catch (error) {
        fail(error?.message || 'Google Maps failed to load.');
      }
    };

    const existing = document.getElementById('google-maps-script');
    if (existing) {
      if (window.google?.maps) {
        finish();
        return;
      }
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', () => fail('Google Maps failed to load.'), { once: true });
      return;
    }

    const callbackName = 'initShipmentTrackingMap';
    window[callbackName] = () => {
      delete window[callbackName];
      finish();
    };

    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&callback=${callbackName}&v=weekly&libraries=geocoding,routes`;
    script.onerror = () => fail('Google Maps failed to load.');
    document.head.appendChild(script);
  });

  return mapsLoader;
};

const geocodeAddress = (maps, queries, country) => new Promise((resolve) => {
  if (!maps?.Geocoder || !Array.isArray(queries) || !queries.length) {
    resolve(null);
    return;
  }

  const geocoder = new maps.Geocoder();
  let index = 0;
  const timer = window.setTimeout(() => resolve({ error: 'TIMEOUT' }), 8000);
  const tryQuery = () => {
    if (index >= queries.length) {
      window.clearTimeout(timer);
      resolve(null);
      return;
    }

    const request = buildGeocodeRequest(queries[index++], country);
    geocoder.geocode(request, (results, status) => {
      if (status === 'REQUEST_DENIED' || status === 'OVER_QUERY_LIMIT') {
        window.clearTimeout(timer);
        resolve({ error: status });
        return;
      }

      const location = status === 'OK' ? results?.[0]?.geometry?.location : null;
      if (location) {
        window.clearTimeout(timer);
        resolve({ latitude: location.lat(), longitude: location.lng() });
        return;
      }
      tryQuery();
    });
  };

  tryQuery();
});

const reverseGeocodePlace = (maps, coordinates) => new Promise((resolve) => {
  if (!maps?.Geocoder || !coordinates) {
    resolve(null);
    return;
  }

  const geocoder = new maps.Geocoder();
  geocoder.geocode({
    location: { lat: coordinates.latitude, lng: coordinates.longitude },
  }, (results, status) => {
    if (status !== 'OK' || !results?.[0]?.address_components) {
      resolve(null);
      return;
    }
    resolve(placeFromAddressComponents(results[0].address_components));
  });
});

const resolveMapStop = async (maps, stop) => {
  const result = await geocodeAddress(maps, stop?.geocodeQueries || [], stop?.country);
  if (result?.error) return { error: result.error };
  if (result?.latitude == null) return null;
  const place = await reverseGeocodePlace(maps, result);
  return { ...stop, coordinates: result, place };
};

const mapGeocodeErrorMessage = (status) => {
  if (status === 'REQUEST_DENIED') {
    return 'Google Geocoding was denied. Enable the Geocoding API for this Maps key and allow this site.';
  }
  if (status === 'OVER_QUERY_LIMIT') {
    return 'Google Geocoding is rate-limited. Wait a moment and refresh the page.';
  }
  if (status === 'TIMEOUT') {
    return 'Google Geocoding timed out before a location could be resolved.';
  }
  return 'No usable shipment location was found to plot on the map.';
};

const MAP_ROUTE_COLOR = '#f36b21';
const MAP_PIN_PATH = 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z';

const toLatLngLiteral = (stop) => ({
  lat: stop.coordinates.latitude,
  lng: stop.coordinates.longitude,
});

const decodeEncodedPolyline = (encoded, precision = 1e5) => {
  if (!encoded) return [];
  const path = [];
  let index = 0;
  let latitude = 0;
  let longitude = 0;

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    latitude += (result & 1) ? ~(result >> 1) : (result >> 1);

    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    longitude += (result & 1) ? ~(result >> 1) : (result >> 1);

    path.push({ lat: latitude / precision, lng: longitude / precision });
  }

  return path;
};

const requestGoogleDirectionsPath = (maps, originPos, destinationPos, waypoint) => new Promise((resolve) => {
  if (!maps.DirectionsService) {
    resolve(null);
    return;
  }

  const timer = window.setTimeout(() => resolve(null), 8000);
  try {
    new maps.DirectionsService().route({
      origin: originPos,
      destination: destinationPos,
      ...(waypoint ? { waypoints: [{ location: waypoint, stopover: false }] } : {}),
      travelMode: maps.TravelMode?.DRIVING || 'DRIVING',
      region: 'IN',
      provideRouteAlternatives: false,
    }, (result, status) => {
      window.clearTimeout(timer);
      const path = result?.routes?.[0]?.overview_path;
      resolve(status === 'OK' && path?.length ? path : null);
    });
  } catch {
    window.clearTimeout(timer);
    resolve(null);
  }
});

const requestGoogleComputeRoutesPath = async (originPos, destinationPos, waypoint) => {
  if (!GOOGLE_MAPS_API_KEY) return null;

  const body = {
    origin: { location: { latLng: { latitude: originPos.lat, longitude: originPos.lng } } },
    destination: { location: { latLng: { latitude: destinationPos.lat, longitude: destinationPos.lng } } },
    travelMode: 'DRIVE',
    routingPreference: 'TRAFFIC_UNAWARE',
    polylineQuality: 'OVERVIEW',
    languageCode: 'en',
    regionCode: 'IN',
  };
  if (waypoint) {
    body.intermediates = [{ location: { latLng: { latitude: waypoint.lat, longitude: waypoint.lng } } }];
  }

  try {
    const response = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
        'X-Goog-FieldMask': 'routes.polyline.encodedPolyline',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) return null;
    const payload = await response.json();
    const encoded = payload?.routes?.[0]?.polyline?.encodedPolyline;
    let path = decodeEncodedPolyline(encoded, 1e5);
    if (path.some((point) => Math.abs(point.lat) > 90 || Math.abs(point.lng) > 180)) {
      path = decodeEncodedPolyline(encoded, 1e6);
    }
    return path.length > 1 ? path : null;
  } catch {
    return null;
  }
};

const requestOsrmDrivingPath = async (points) => {
  const coords = points
    .filter((point) => Number.isFinite(point?.lat) && Number.isFinite(point?.lng))
    .map((point) => `${point.lng},${point.lat}`)
    .join(';');
  if (!coords.includes(';')) return null;

  try {
    const response = await fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`);
    if (!response.ok) return null;
    const payload = await response.json();
    const coordinates = payload?.routes?.[0]?.geometry?.coordinates;
    if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
    return coordinates.map(([lng, lat]) => ({ lat, lng }));
  } catch {
    return null;
  }
};

const sameMapPoint = (left, right) => (
  Number.isFinite(left?.lat)
  && Number.isFinite(left?.lng)
  && left.lat === right?.lat
  && left.lng === right?.lng
);

const fallbackStraightPath = (originPos, destinationPos) => {
  if (!originPos || !destinationPos || sameMapPoint(originPos, destinationPos)) return null;
  return [originPos, destinationPos];
};

const requestOriginDestinationPath = async (maps, originPos, destinationPos) => (
  await requestGoogleDirectionsPath(maps, originPos, destinationPos, null)
  || await requestGoogleComputeRoutesPath(originPos, destinationPos, null)
  || await requestOsrmDrivingPath([originPos, destinationPos])
  || fallbackStraightPath(originPos, destinationPos)
);

const resolveGroundRoutePath = async (maps, originPos, destinationPos, currentPos) => {
  if (!originPos || !destinationPos) return null;

  // When the backend has not reported a current location yet, always draw the
  // origin → destination ground route (road path, or a straight fallback).
  if (currentPos) {
    const viaCurrent = await requestGoogleDirectionsPath(maps, originPos, destinationPos, currentPos)
      || await requestGoogleComputeRoutesPath(originPos, destinationPos, currentPos)
      || await requestOsrmDrivingPath([originPos, currentPos, destinationPos]);
    if (viaCurrent?.length > 1) return viaCurrent;
  }

  return requestOriginDestinationPath(maps, originPos, destinationPos);
};

const drawGroundRoute = (maps, map, path) => {
  const polyline = new maps.Polyline({
    map,
    path,
    geodesic: false,
    strokeColor: MAP_ROUTE_COLOR,
    strokeOpacity: 0.95,
    strokeWeight: 5,
  });
  const routeBounds = new maps.LatLngBounds();
  path.forEach((point) => routeBounds.extend(point));
  map.fitBounds(routeBounds, 72);
  return polyline;
};

const createStopLabel = (maps, { map, position, text, color }) => {
  class ShipmentMapLabel extends maps.OverlayView {
    onAdd() {
      this.div = document.createElement('div');
      this.div.className = 'shipment-map-label';
      this.div.style.setProperty('--label-color', color);
      const title = document.createElement('b');
      title.textContent = text;
      this.div.appendChild(title);
      this.getPanes().floatPane.appendChild(this.div);
    }

    draw() {
      const projection = this.getProjection();
      if (!projection || !this.div) return;
      const point = projection.fromLatLngToDivPixel(position);
      if (!point) return;
      this.div.style.left = `${point.x}px`;
      this.div.style.top = `${point.y}px`;
    }

    onRemove() {
      this.div?.remove();
      this.div = null;
    }
  }

  const overlay = new ShipmentMapLabel();
  overlay.setMap(map);
  return overlay;
};

const mapStopsSignature = (stops) => JSON.stringify((Array.isArray(stops) ? stops : []).map((stop) => ({
  type: stop.type,
  kind: stop.kind,
  queries: stop.geocodeQueries,
})));

const placesAreEqual = (left, right) => (
  left?.origin === right?.origin
  && left?.current === right?.current
  && left?.destination === right?.destination
);

const ShipmentJourneyMap = ({ stops, onPlacesResolved }) => {
  const mapContainer = useRef(null);
  const [mapError, setMapError] = useState('');
  const stopKey = mapStopsSignature(stops);
  const stopsRef = useRef(stops);
  const onPlacesResolvedRef = useRef(onPlacesResolved);
  stopsRef.current = stops;
  onPlacesResolvedRef.current = onPlacesResolved;

  useEffect(() => {
    let mounted = true;
    let mapInstance = null;
    let overlays = [];
    let routePolyline = null;
    const mapStops = stopsRef.current;

    loadGoogleMaps().then(async (maps) => {
      if (!mounted || !mapContainer.current) return;
      setMapError('');

      const resolvedResults = await Promise.all((mapStops || []).map((stop) => resolveMapStop(maps, stop)));
      if (!mounted || !mapContainer.current) return;

      const geocodeError = resolvedResults.find((stop) => stop?.error)?.error;
      const resolvedStops = resolvedResults.filter((stop) => stop?.coordinates);
      if (!resolvedStops.length) {
        setMapError(mapGeocodeErrorMessage(geocodeError));
        return;
      }

      const pickupStop = resolvedStops.find((stop) => stop.kind === 'origin');
      const currentStop = resolvedStops.find((stop) => stop.kind === 'current');
      const deliveryStop = resolvedStops.find((stop) => stop.kind === 'destination');
      const nextPlaces = resolvedStops.reduce((places, stop) => {
        const label = formatCityStateCountry(stop.place);
        if (stop.kind && label) places[stop.kind] = label;
        return places;
      }, {});
      onPlacesResolvedRef.current?.((previous) => (
        placesAreEqual(previous, nextPlaces) ? previous : nextPlaces
      ));
      const centerPoint = currentStop || pickupStop || resolvedStops[0];
      mapInstance = new maps.Map(mapContainer.current, {
        center: { lat: centerPoint.coordinates.latitude, lng: centerPoint.coordinates.longitude },
        zoom: resolvedStops.length > 1 ? 8 : 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        clickableIcons: false,
        styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
      });
      const bounds = new maps.LatLngBounds();
      const infoWindow = new maps.InfoWindow();

      resolvedStops.forEach((point, index) => {
        const position = toLatLngLiteral(point);
        bounds.extend(position);
        const marker = new maps.Marker({
          map: mapInstance,
          position,
          title: `${point.type}: ${point.label || `${position.lat}, ${position.lng}`}`,
          zIndex: point.kind === 'current' ? 4 : index + 1,
          icon: {
            path: MAP_PIN_PATH,
            fillColor: point.color,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: 1.2,
            anchor: new maps.Point(0, 0),
          },
        });
        overlays.push(createStopLabel(maps, {
          map: mapInstance,
          position: new maps.LatLng(position.lat, position.lng),
          text: point.type,
          color: point.color,
        }));
        marker.addListener('click', () => {
          const content = document.createElement('div');
          content.className = 'shipment-map-info';
          const title = document.createElement('strong');
          title.textContent = point.type;
          content.appendChild(title);
          content.appendChild(document.createElement('br'));
          content.appendChild(document.createTextNode(point.label || `${position.lat}, ${position.lng}`));
          infoWindow.setContent(content);
          infoWindow.open({ map: mapInstance, anchor: marker });
        });
      });

      let fittedToRoute = false;
      if (pickupStop && deliveryStop) {
        const routePath = await resolveGroundRoutePath(
          maps,
          toLatLngLiteral(pickupStop),
          toLatLngLiteral(deliveryStop),
          currentStop ? toLatLngLiteral(currentStop) : null,
        );
        if (!mounted || !mapInstance) return;
        if (routePath?.length > 1) {
          routePolyline = drawGroundRoute(maps, mapInstance, routePath);
          fittedToRoute = true;
        }
      }

      if (!fittedToRoute && resolvedStops.length > 1) {
        maps.event.addListenerOnce(mapInstance, 'bounds_changed', () => {
          if (mapInstance.getZoom() > 12) mapInstance.setZoom(12);
        });
        mapInstance.fitBounds(bounds, 72);
      }
      maps.event.trigger(mapInstance, 'resize');
    }).catch((error) => {
      if (mounted) setMapError(error.message || 'Unable to load Google Maps.');
    });

    return () => {
      mounted = false;
      overlays.forEach((overlay) => overlay.setMap(null));
      routePolyline?.setMap(null);
      mapInstance = null;
    };
  }, [stopKey]);

  return (
    <div className="route-map shipment-google-map" ref={mapContainer}>
      {mapError && (
        <div className="map-error">
          <p>{mapError}</p>
          {Boolean(stops?.length) && (
            <ul className="map-fallback-points">
              {stops.map((stop) => (
                <li key={stop.type}>
                  <b>{stop.type}</b>
                  <span>{stop.label || 'Location unavailable'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const formatAddressLines = (value) => {
  if (!hasRecordValues(value)) return [];

  const visibleTerms = [
    value.city,
    value.state,
    value.province,
    value.provinceCode,
    value.country,
    value.countryCode,
    value.countryCodeV2,
    value.landmark,
    value.locality,
    value.area,
    value.district,
  ].filter(hasValue);

  const cityState = [value.city, value.state || value.province || value.provinceCode]
    .filter(hasValue)
    .map(String)
    .join(', ');
  const cityLine = [cityState, value.zipcode || value.zip || value.postcode]
    .filter(hasValue)
    .map(String)
    .join(' - ');

  return [
    value.company,
    maskAddressLine(value.address1, visibleTerms),
    maskAddressLine(value.address2, visibleTerms),
    cityLine,
    value.country || value.countryCode || value.countryCodeV2,
  ].filter(hasValue).map(String);
};

const fullName = (...records) => {
  for (const record of records) {
    if (!isRecord(record)) continue;
    const name = [record.firstName, record.lastName].filter(hasValue).join(' ').trim();
    if (name) return name;
  }
  return 'Not available';
};

const statusIcon = (presentation, category, code) => {
  const icon = String(presentation?.icon || '').toLowerCase();
  const aliases = {
    package: 'box',
    warehouse: 'box',
    bike: 'van',
    alert: 'home',
    return: 'route',
    x: 'home',
    pause: 'box',
    clock: 'route',
    help: 'route',
  };

  if (aliases[icon]) return aliases[icon];
  if (['clipboard', 'box', 'truck', 'van', 'route', 'home', 'check'].includes(icon)) return icon;
  if (category === 'DELIVERED' || code === 'DEL') return 'check';
  return 'route';
};

const PROGRESS_ALERT_CODES = new Set(['UND', 'EXC', 'RTO', 'RIT', 'ROD', 'RUN', 'RTD']);

const progressClass = (step) => {
  const code = String(step.code || '').trim().toUpperCase();
  const color = String(step.presentation?.color || '').toLowerCase();
  const stateClass = step.state === 'CURRENT'
    ? 'current'
    : step.state === 'COMPLETED'
      ? 'done'
      : 'upcoming';
  const isFailed = color === 'red'
    || FAILED_STATUS_CODES.has(code)
    || code === 'UND'
    || code === 'EXC'
    || code === 'RUN';
  const isReturn = !isFailed && (
    color === 'orange'
    || (RETURN_STATUS_CODES.has(code) && code !== 'RTD')
  );
  return [stateClass, isFailed ? 'failed' : '', isReturn ? 'return' : ''].filter(Boolean).join(' ');
};

const timelineClass = (event) => {
  const codes = eventStatusCodes(event);
  const group = eventGroupCode(event);
  const category = String(event.status?.category || event.category || '').toUpperCase();
  if (codes.includes('RTD') && !codes.includes('RUN')) return 'return';
  if (
    codes.some((code) => FAILED_STATUS_CODES.has(code))
    || group === 'UND'
    || group === 'EXC'
    || group === 'RUN'
    || category === 'EXCEPTION'
    || category === 'CANCELLED'
  ) {
    return 'failed';
  }
  if (
    codes.some((code) => RETURN_STATUS_CODES.has(code))
    || group === 'RTO'
    || category === 'RETURN'
  ) {
    return 'return';
  }
  return 'done';
};

const GROUP_ICONS = {
  ORD: 'clipboard',
  PCK: 'box',
  OFP: 'truck',
  PKD: 'truck',
  INT: 'route',
  RAD: 'box',
  OFD: 'van',
  UND: 'home',
  ATTEMPT: 'home',
  DEL: 'check',
  FINAL: 'check',
  RTO: 'route',
  RTD: 'box',
  RUN: 'home',
  EXC: 'home',
};

const RETURN_STATUS_CODES = new Set(['RTO', 'RIT', 'ROD', 'RUN', 'RTD']);
const FAILED_STATUS_CODES = new Set([
  'RUN', 'UND', 'DLA', 'DPC', 'CTR', 'CRE', 'CUN', 'ADI', 'ODA', 'DEX',
  'PNR', 'MNR', 'EAP', 'DNA', 'MIS', 'NDD', 'PKF',
]);
const SUCCESS_STATUS_CODES = new Set(['DEL', 'RTD']);

const customerFacingStatusLabel = (code, candidate, fallback = 'Shipment Update') => {
  const text = hasValue(candidate) ? String(candidate).trim() : '';
  // Never display an opaque database/carrier code such as INT, SPD, or IND
  // as the customer-facing shipment status.
  if (!text || /^[A-Z0-9]{2,5}$/.test(text.toUpperCase())) return fallback;
  return text;
};

const eventStatusCodes = (event) => [
  event?.status?.code,
  event?.code,
  event?.rawStatus,
  event?.rawScan,
  event?.statusGroup,
].filter(hasValue).map((value) => String(value).trim().toUpperCase());

const eventGroupCode = (event) => {
  const fromGroup = String(event?.group?.code || event?.lifecycleGroup || '').trim().toUpperCase();
  return fromGroup || null;
};

const eventMatchesGroup = (event, groupCode) => {
  const lifecycleGroup = eventGroupCode(event);
  if (lifecycleGroup) return lifecycleGroup === groupCode;
  return eventStatusCodes(event).includes(groupCode);
};

const SCAN_ACRONYMS = new Set(['AWB', 'COD', 'NDR', 'OTP', 'POD', 'RTO']);
const SCAN_ACRONYM_LABELS = {
  AWB: 'Air waybill',
  COD: 'Cash on delivery',
  DRC: 'Shipment data received',
  NDR: 'Non-delivery report',
  OTP: 'One-time password',
  POD: 'Proof of delivery',
  RTO: 'Return to origin',
};

const isRawCarrierToken = (value) => {
  const source = String(value || '').trim();
  if (!source) return true;
  if (/_$/.test(source) || /^_/.test(source) || /__/.test(source)) return true;
  return /^[A-Z0-9]{2,8}$/i.test(source);
};

const isIncompleteScanPhrase = (value) => {
  const source = String(value || '').trim();
  if (!source) return true;
  if (/^(delivered to|out for|arrived at|reached at)$/i.test(source)) return true;
  return /\b(to|for|at|from|with|in|on)$/i.test(source) && source.split(/\s+/).length <= 3;
};

const formatCarrierScan = (value) => {
  if (!hasValue(value)) return null;

  const source = String(value).trim();
  if (isRawCarrierToken(source) || isIncompleteScanPhrase(source)) return null;
  const sourceUpper = source.toUpperCase();
  if (SCAN_ACRONYM_LABELS[sourceUpper]) return SCAN_ACRONYM_LABELS[sourceUpper];
  // A short carrier code without words has no safe customer-facing meaning.
  if (/^[A-Z0-9]{2,5}$/.test(sourceUpper) && !/[\s_-]/.test(source)) return null;

  const words = source
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean);

  if (!words.length) return null;

  const normalized = words.map((word, index) => {
    const upper = word.toUpperCase();
    if (SCAN_ACRONYMS.has(upper)) return upper;
    const lower = word === upper ? word.toLowerCase() : word;
    return index === 0
      ? `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`
      : lower;
  });

  return normalized.join(' ');
};

const normalizeScanPhrase = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const isGenericStatusScan = (scanText, event) => {
  const scan = normalizeScanPhrase(scanText);
  if (!scan) return true;
  const description = normalizeScanPhrase(event?.description);
  const name = normalizeScanPhrase(event?.name || event?.status?.name);
  if (description && (scan === description || description.includes(scan) || scan.includes(description))) return true;
  if (name && (scan === name || name.includes(scan) || scan.includes(name))) return true;
  return false;
};

const customerScanKey = (event, scanText) => {
  const scan = isGenericStatusScan(scanText, event) ? 'generic' : normalizeScanPhrase(scanText);
  const location = normalizeScanPhrase(event?.location?.label || event?.location?.city || '');
  return [String(event?.code || event?.status?.code || '').trim().toUpperCase(), scan, location].join('|');
};

const eventChildStatus = (event, groupCode) => {
  const eventCode = String(event?.code || event?.status?.code || '').trim().toUpperCase();
  const parentCode = String(groupCode || eventGroupCode(event) || '').trim().toUpperCase();
  return Boolean(eventCode && parentCode && eventCode !== parentCode);
};

const customerTimelineEvents = (events, groupCode = null) => {
  const unique = new Map();
  for (const event of Array.isArray(events) ? events : []) {
    const rawScan = event?.rawScan || event?.scan?.name;
    const isChildStatus = eventChildStatus(event, groupCode);
    const scanText = formatCarrierScan(rawScan)
      || (hasValue(rawScan) ? formatCarrierScan(event?.description) : null)
      || (isChildStatus
        ? (formatCarrierScan(event?.name)
          || formatCarrierScan(event?.status?.name)
          || formatCarrierScan(event?.description))
        : null);
    if (!scanText) continue;
    const key = customerScanKey(event, scanText);
    const existing = unique.get(key);
    const eventQuality = formatCarrierScan(event?.rawScan || event?.scan?.name) ? 2 : 1;
    const existingQuality = formatCarrierScan(existing?.rawScan || existing?.scan?.name) ? 2 : 0;
    const eventTime = Date.parse(event?.occurredAt || '') || 0;
    const existingTime = Date.parse(existing?.occurredAt || '') || 0;
    if (!existing || eventQuality > existingQuality || (eventQuality === existingQuality && eventTime >= existingTime)) {
      unique.set(key, event);
    }
  }
  return [...unique.values()];
};

/**
 * Build timeline/progress groups from backend-provided progress + per-event group.
 * Progress labels stay as main lifecycle groups (Undelivered, not Consignee Unavailable).
 * Child scans belong in the timeline events only.
 */
const buildTimelineGroups = (timeline, progress, currentStatus) => {
  const currentCode = String(currentStatus?.code || '').trim().toUpperCase();
  const currentCategory = String(currentStatus?.category || '').trim().toUpperCase();
  const isReturnJourney = currentCategory === 'RETURN' || RETURN_STATUS_CODES.has(currentCode);
  const journeyProgress = isReturnJourney
    ? progress
    : progress.filter((step) => !RETURN_STATUS_CODES.has(String(step?.code || '').trim().toUpperCase()));
  const journeyTimeline = isReturnJourney
    ? timeline
    : timeline.filter((event) => !RETURN_STATUS_CODES.has(eventGroupCode(event)));

  const definitions = resolveGroupDefinitions(journeyProgress, journeyTimeline, currentStatus);
  let groups = definitions.map((definition) => {
    const events = journeyTimeline.filter((event) => eventMatchesGroup(event, definition.code));
    const progressHint = journeyProgress.find((step) => String(step?.code || '').trim().toUpperCase() === definition.code);
    const latestEvent = events[events.length - 1];
    const backendState = String(progressHint?.state || '').toUpperCase();
    const customerScans = customerTimelineEvents(events, definition.code);
    const containsCurrent = Boolean(
      backendState === 'CURRENT'
      || definition.code === currentCode
      || (currentStatus?.group?.code && definition.code === String(currentStatus.group.code).toUpperCase())
      || events.some((event) => event.isCurrent)
      || (currentCode && events.some((event) => eventStatusCodes(event).includes(currentCode))),
    );
    const reached = containsCurrent
      || backendState === 'COMPLETED'
      || customerScans.length > 0;
    const displayCode = containsCurrent ? (currentCode || definition.code) : (latestEvent?.status?.code || latestEvent?.code || definition.code);
    const tone = FAILED_STATUS_CODES.has(displayCode)
      || FAILED_STATUS_CODES.has(definition.code)
      || definition.code === 'UND'
      || definition.code === 'EXC'
      || (containsCurrent && ['EXCEPTION', 'CANCELLED'].includes(currentCategory))
      ? 'failed'
      : SUCCESS_STATUS_CODES.has(displayCode) || definition.code === 'RTD'
        ? 'success'
        : (RETURN_STATUS_CODES.has(displayCode) || RETURN_STATUS_CODES.has(definition.code)) && definition.code !== 'RTD'
          ? 'return'
          : latestEvent
            ? timelineClass(latestEvent)
            : 'normal';

    let state = 'upcoming';
    if (containsCurrent || backendState === 'CURRENT') state = 'current';
    else if (reached) state = 'done';

    return {
      code: definition.code,
      label: definition.label,
      icon: definition.icon || GROUP_ICONS[definition.code] || 'route',
      order: definition.order,
      events,
      latestEvent,
      lifecycleLabel: latestEvent
        ? customerFacingStatusLabel(
          latestEvent.status?.code || latestEvent.code || latestEvent.rawStatus,
          latestEvent.status?.name || latestEvent.name || latestEvent.rawStatusName,
          null,
        )
        : null,
      description: latestEvent?.description
        || (containsCurrent ? currentStatus?.description : null)
        || definition.label,
      state,
      displayCode,
      tone,
    };
  });

  const hasReachedGroup = (group) => group.state === 'done'
    || group.state === 'current'
    || customerTimelineEvents(group.events, group.code).length > 0;
  const furthestReachedIndex = groups.reduce(
    (furthest, group, index) => (hasReachedGroup(group) ? index : furthest),
    -1,
  );
  let currentGroupIndex = groups.findIndex((group) => group.state === 'current');
  // The API currentStatus is authoritative. Historical scans in a later
  // lifecycle group must not move the customer-facing current step forward.
  if (furthestReachedIndex > currentGroupIndex && currentGroupIndex >= 0 && !currentCode) {
    const currentGroup = groups[currentGroupIndex];
    const furthestGroup = groups[furthestReachedIndex];
    const currentIsInNetwork = ['ORD', 'PCK', 'PKD', 'INT'].includes(currentGroup.code);
    const furthestIsLeftoverException = ['UND', 'EXC'].includes(furthestGroup.code);
    // An older MIS/UND scan must not pull current off a later In Transit update.
    // OFD after a failed attempt still promotes UND (last-mile reattempt).
    if (!(currentIsInNetwork && furthestIsLeftoverException)) {
      groups = groups.map((group, index) => {
        if (index === furthestReachedIndex) return { ...group, state: 'current' };
        if (index === currentGroupIndex) return { ...group, state: 'done' };
        return group;
      });
      currentGroupIndex = furthestReachedIndex;
    }
  }

  return groups.map((group, index) => {
    if (currentGroupIndex >= 0 && index > currentGroupIndex) {
      const alertTone = group.tone === 'failed' || group.tone === 'return'
        || PROGRESS_ALERT_CODES.has(group.code)
        ? (group.tone === 'return' || RETURN_STATUS_CODES.has(group.code) ? 'return' : 'failed')
        : 'normal';
      return {
        ...group,
        state: 'upcoming',
        tone: alertTone,
        inferred: false,
      };
    }

    const inferredPrerequisite = group.state === 'upcoming' && furthestReachedIndex > index;
    return {
      ...group,
      state: inferredPrerequisite ? 'done' : group.state,
      inferred: inferredPrerequisite,
    };
  });
};

const resolveGroupDefinitions = (progress, timeline, currentStatus) => {
  const fromProgress = (Array.isArray(progress) ? progress : [])
    .map((step, index) => {
      const code = String(step?.code || '').trim().toUpperCase();
      if (!code) return null;
      const label = step.name || step.label || code;
      return {
        code,
        label: String(label),
        order: Number.isFinite(Number(step.order)) ? Number(step.order) : (index + 1) * 10,
        icon: GROUP_ICONS[code] || step.presentation?.icon || 'route',
      };
    })
    .filter(Boolean);

  if (fromProgress.length) {
    return [...fromProgress].sort((left, right) => left.order - right.order || left.code.localeCompare(right.code));
  }

  const fromTimeline = [];
  const seen = new Set();
  for (const event of Array.isArray(timeline) ? timeline : []) {
    const code = eventGroupCode(event);
    if (!code || seen.has(code)) continue;
    seen.add(code);
    fromTimeline.push({
      code,
      label: event.group?.name || event.status?.name || event.name || code,
      order: Number.isFinite(Number(event.group?.order)) ? Number(event.group.order) : (fromTimeline.length + 1) * 10,
      icon: GROUP_ICONS[code] || 'route',
    });
  }

  if (fromTimeline.length)
    return fromTimeline.sort((left, right) => left.order - right.order || left.code.localeCompare(right.code));

  const currentCode = String(currentStatus?.code || '').trim().toUpperCase();
  if (!currentCode) return [];
  return [{
    code: currentCode,
    label: customerFacingStatusLabel(currentCode, currentStatus?.name, currentCode),
    order: 10,
    icon: GROUP_ICONS[currentCode] || 'route',
  }];
};

const progressStateText = (step) => {
  if (step.state === 'COMPLETED') return 'Completed';
  if (step.state === 'CURRENT') return 'Current';
  return 'Upcoming';
};

function TrackingFlow({ shipment, onTrackAnother, onOpenAdmin }) {
  const [copyMessage, setCopyMessage] = useState('');
  const [resolvedPlaces, setResolvedPlaces] = useState({});

  useEffect(() => {
    setResolvedPlaces({});
  }, [shipment.trackingNumber]);
  const customer = isRecord(shipment.customer) ? shipment.customer : {};
  const journey = isRecord(shipment.journey) ? shipment.journey : {};
  // Preferred source: shipmentMap (origin -> current -> destination). Lat/lng
  // are approximated in the browser from pincode, then city. Fall back to the
  // legacy journey shape.
  const shipmentMap = isRecord(shipment.shipmentMap) ? shipment.shipmentMap : {};
  const originStop = hasRecordValues(shipmentMap.origin)
    ? shipmentMap.origin
    : (hasRecordValues(journey.origin) ? journey.origin : {});
  // The map pin stays city-level, while the details panel shows the full
  // recipient address from the Shipra order.
  const destinationStop = hasRecordValues(shipmentMap.destination)
    ? shipmentMap.destination
    : (hasRecordValues(shipment.deliveryAddress)
      ? shipment.deliveryAddress
      : (hasRecordValues(journey.destination) ? journey.destination : {}));
  const deliveryAddress = hasRecordValues(shipment.deliveryAddress)
    ? shipment.deliveryAddress
    : destinationStop;
  const currentLocation = hasRecordValues(shipmentMap.currentLocation)
    ? shipmentMap.currentLocation
    : (hasRecordValues(shipment.currentLocation)
      ? shipment.currentLocation
      : (hasRecordValues(journey.currentLocation) ? journey.currentLocation : {}));
  const currentStatus = isRecord(shipment.currentStatus) ? shipment.currentStatus : null;
  const progress = Array.isArray(shipment.progress) ? shipment.progress : [];
  const timeline = Array.isArray(shipment.timeline) ? shipment.timeline : [];
  const timelineGroups = buildTimelineGroups(timeline, progress, currentStatus);
  const visibleTimelineGroups = timelineGroups.map((group) => (
    group.state === 'upcoming'
      ? { ...group, events: [], latestEvent: null, lifecycleLabel: null }
      : group
  ));
  const visibleProgress = timelineGroups.map((group) => ({
    code: group.code,
    label: group.label,
    state: group.state === 'current'
      ? 'CURRENT'
      : group.state === 'done'
        ? 'COMPLETED'
        : 'UPCOMING',
    presentation: {
      icon: group.icon,
      color: group.tone === 'failed'
        ? 'red'
        : group.tone === 'return'
          ? 'orange'
          : 'green',
    },
    occurredAt: group.latestEvent?.occurredAt || (group.state === 'current' ? currentStatus?.occurredAt : null),
    lifecycleLabel: group.lifecycleLabel,
  }));
  const addressLines = formatAddressLines(deliveryAddress);
  const customerName = fullName(customer, deliveryAddress);
  const currentLocationLabel = resolvedPlaces.current
    || formatCityStateCountry(currentLocation);
  const originLabel = resolvedPlaces.origin
    || formatCityStateCountry(originStop)
    || formatCityStateCountry(journey.origin);
  const destinationLabel = resolvedPlaces.destination
    || formatCityStateCountry(destinationStop)
    || formatCityStateCountry(journey.destination);
  const mapStops = useMemo(() => [
    { value: originStop, label: originLabel, type: 'Pickup', color: '#e51f3b', kind: 'origin' },
    { value: currentLocation, label: currentLocationLabel, type: 'Current Location', color: '#f36b21', kind: 'current' },
    { value: destinationStop, label: destinationLabel, type: 'Delivery', color: '#159766', kind: 'destination' },
  ].filter((candidate) => candidate.kind === 'current'
    ? hasRecordValues(candidate.value)
    : (hasRecordValues(candidate.value) || hasValue(candidate.label)))
    .map((candidate) => {
      const geocodeQueries = buildGeocodeQueries(candidate.value, candidate.kind);
      return {
        ...candidate,
        country: candidate.value?.country || candidate.value?.countryCode || candidate.value?.countryCodeV2,
        geocodeQueries,
      };
    })
    .filter((candidate) => candidate.geocodeQueries.length), [
    originStop,
    currentLocation,
    destinationStop,
    originLabel,
    currentLocationLabel,
    destinationLabel,
  ]);
  const currentTimelineGroup = timelineGroups.find((group) => group.state === 'current') || null;
  const currentCode = String(
    currentTimelineGroup?.displayCode || currentStatus?.code || '',
  ).trim().toUpperCase();
  const statusCategory = String(currentStatus?.category || '').toUpperCase();
  const isDelivered = statusCategory === 'DELIVERED' || SUCCESS_STATUS_CODES.has(currentCode);
  const isFailed = currentTimelineGroup?.tone === 'failed'
    || FAILED_STATUS_CODES.has(currentCode)
    || ['EXCEPTION', 'CANCELLED'].includes(statusCategory);
  const isException = isFailed || currentTimelineGroup?.tone === 'return'
    || statusCategory === 'RETURN'
    || RETURN_STATUS_CODES.has(currentCode);
  const currentProgressStep = visibleProgress.find((step) => String(step?.state || '').toUpperCase() === 'CURRENT')
    || progress.find((step) => String(step?.state || '').toUpperCase() === 'CURRENT');
  const statusName = currentTimelineGroup
    ? customerFacingStatusLabel(
      currentCode,
      currentTimelineGroup.label,
      currentStatus?.name || 'Shipment Update',
    )
    : currentStatus
      ? customerFacingStatusLabel(
        currentCode,
        currentStatus?.name,
        currentProgressStep?.label || currentProgressStep?.name || 'Shipment Update',
      )
      : 'Shipment found';
  const statusHeading = isDelivered ? `${statusName} Successfully` : statusName;
  const statusDescription = currentStatus?.description || 'Your shipment details have been loaded.';
  const deliveryDate = shipment.deliveredAt || shipment.estimatedDeliveryAt;
  const lastUpdatedAt = shipment.lastUpdatedAt || currentStatus?.occurredAt;
  // Internal Shipra signatures intentionally never enter the customer share URL.
  const shareUrl = buildTrackingShareUrl(shipment.trackingNumber);
  const carrierName = shipment.carrier?.name || shipment.carrier?.code;
  const shareMessage = [
    'Shipra shipment update',
    `Tracking number: ${shipment.trackingNumber}`,
    carrierName ? `Carrier: ${carrierName}` : null,
    currentStatus ? `Current status: ${statusName}` : null,
    lastUpdatedAt ? `Last updated: ${formatDate(lastUpdatedAt)}` : null,
    shareUrl ? `Track shipment: ${shareUrl}` : null,
  ].filter(Boolean).join('\n');
  const encodedShareMessage = encodeURIComponent(shareMessage);
  const whatsappShareUrl = `https://wa.me/?text=${encodedShareMessage}`;
  const smsShareUrl = `sms:?body=${encodedShareMessage}`;
  const emailShareUrl = `mailto:?subject=${encodeURIComponent(`Shipment tracking: ${shipment.trackingNumber}`)}&body=${encodedShareMessage}`;
  const journeyState = isDelivered ? 'is-delivered' : isException ? 'is-exception' : 'is-active';

  const handleCopy = async () => {
    if (!shareUrl || !navigator.clipboard?.writeText) {
      setCopyMessage('Copy is not available in this browser.');
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyMessage('Copied');
    } catch {
      setCopyMessage('Unable to copy');
    }
  };

  return (
    <div className="tracking-flow-page">
      <header className="site-header navbar navbar-expand-lg bg-white">
        <div className="container-fluid px-3 px-md-5">
          <a className="navbar-brand brand-wrap d-flex align-items-center" href="#top" aria-label="Shipra home">
            <img className="shipra-logo" src={shipraLogo} alt="Shipra Logistics" />
          </a>

          <button className="navbar-toggler border-0 shadow-none" type="button" data-bs-toggle="collapse" data-bs-target="#trackingFlowNav" aria-controls="trackingFlowNav" aria-expanded="false" aria-label="Toggle navigation">
            <span className="navbar-toggler-icon" />
          </button>

          <div className="collapse navbar-collapse" id="trackingFlowNav">
            <nav className="navbar-nav mx-auto align-items-lg-center">
              <button className="nav-link nav-action active" type="button" onClick={onTrackAnother}>Track Another Shipment</button>
              <a className="nav-link" href="https://www.shipra.org/" target="_blank" rel="noreferrer">Services</a>
              <a className="nav-link" href="https://www.shipra.org/about-us/" target="_blank" rel="noreferrer">About Us</a>
              <a className="nav-link" href="https://www.shipra.org/contact/" target="_blank" rel="noreferrer">Contact Us</a>
            </nav>

            <div className="header-actions">
              <div className="dropdown language-menu">
                <button className="btn border-0 dropdown-toggle d-flex align-items-center gap-2" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <FlowIcon name="globe" size={25} />
                  <b>EN</b>
                </button>
                <ul className="dropdown-menu dropdown-menu-end border-0 shadow">
                  <li><button className="dropdown-item" type="button">English</button></li>
                </ul>
              </div>
              {hasAdminAccess() && (
                <button
                  className="admin-nav-button"
                  type="button"
                  onClick={onOpenAdmin}
                  aria-label="Open shipment status admin review"
                >
                  <FlowIcon name="admin" size={21} />
                  <span>Admin</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="flow-main" id="top">
        <section className={`delivery-summary ${isFailed ? 'status-failed' : isDelivered ? 'status-delivered' : isException ? 'status-exception' : 'status-active'}`}>
          <div className="delivery-heading">
            <span className="success-circle">
              <FlowIcon name={statusIcon(currentStatus?.presentation, statusCategory, currentStatus?.code)} size={42} />
            </span>
            <div><h1>{statusHeading}</h1><p>{statusDescription}</p></div>
          </div>
          <div className="row g-0 summary-grid">
            <div className="col-6 col-lg summary-item"><span>Tracking Number</span><strong>{shipment.trackingNumber}</strong></div>
            <div className="col-6 col-lg summary-item"><span>Order Number</span><strong>{shipment.orderNumber || shipment.orderId || 'Not available'}</strong></div>
            <div className="col-6 col-lg summary-item"><span>Courier Partner</span><strong>{shipment.carrier?.name || shipment.carrier?.code || 'Not available'}{shipment.serviceType && <small>{shipment.serviceType}</small>}</strong></div>
            <div className="col-6 col-lg summary-item"><span>{shipment.deliveredAt ? 'Delivered On' : 'Estimated Delivery'}</span><strong>{formatDate(deliveryDate)}</strong></div>
            <div className="col-12 col-lg summary-item"><span>Last Updated</span><strong>{formatDate(lastUpdatedAt)}</strong></div>
          </div>
        </section>

        <section className="flow-card progress-card">
          <h2>Shipment Progress</h2>
          {visibleProgress.length ? (
            <div className="progress-steps" style={{ gridTemplateColumns: `repeat(${visibleProgress.length}, minmax(120px, 1fr))` }}>
              {visibleProgress.map((step, index) => {
                const stateClass = progressClass(step);
                return (
                  <div className={`progress-step ${stateClass}`} key={`${step.code || 'step'}-${index}`}>
                    <div className="step-icon"><FlowIcon name={statusIcon(step.presentation, null, step.code)} size={28} /></div>
                    <span className="step-state">{
                      /\b(failed|return)\b/.test(stateClass)
                        ? '!'
                        : /\bupcoming\b/.test(stateClass)
                          ? '•'
                          : '✓'
                    }</span>
                    <strong>{step.label || step.code || 'Shipment update'}</strong>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="flow-empty-state">Detailed shipment progress is not available yet.</p>
          )}
        </section>

        <div className="row g-3 dashboard-row">
          <div className="col-12 col-xl-4">
            <section className="flow-card journey-card h-100">
              <div className="card-title-row"><h2>Shipment Journey</h2></div>
              {mapStops.length ? (
                <ShipmentJourneyMap stops={mapStops} onPlacesResolved={setResolvedPlaces}/>
              ) : (
                <div className="route-map route-map-placeholder is-empty">
                  <FlowIcon name="location" size={44}/>
                  <span>Latest carrier location</span>
                  <strong>Map is unavailable because no shipment location or address was reported.</strong>
                </div>
              )}
              <div className={`movement api-journey ${journeyState}${currentLocationLabel ? ' has-current' : ''}`}>
                <h3>Shipment Movement</h3>
                {currentLocationLabel ? (
                  <>
                    <div className="movement-line">
                      <span className="movement-marker pickup-marker"><FlowIcon name="van" size={30}/></span>
                      <i className="pickup-route"/>
                      <span className="movement-marker transit-marker"><FlowIcon name="location" size={27}/></span>
                      <i className="delivery-route"/>
                      <span className="movement-marker delivered-marker"><FlowIcon name="location" size={27}/></span>
                    </div>
                    <div className="movement-labels">
                      <p><b>{originLabel || 'Origin unavailable'}</b><small>Origin</small></p>
                      <p><b>{currentLocationLabel}</b><small>Latest reported location</small></p>
                      <p><b>{destinationLabel || 'Destination unavailable'}</b><small>Destination</small></p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="movement-line movement-line-simple">
                      <span className="movement-marker pickup-marker"><FlowIcon name="van" size={30}/></span>
                      <i/>
                      <span className="movement-marker delivered-marker"><FlowIcon name="location" size={27}/></span>
                    </div>
                    <div className="movement-labels movement-labels-simple">
                      <p><b>{originLabel || 'Origin unavailable'}</b><small>Origin</small></p>
                      <p><b>{destinationLabel || 'Destination unavailable'}</b><small>Destination</small></p>
                    </div>
                  </>
                )}
              </div>
            </section>
          </div>

          <div className="col-12 col-xl-5 timeline-column">
            <section className="flow-card timeline-card timeline-column-card">
              <h2>Shipment Timeline (Detailed History)</h2>
              <div className="timeline-groups" data-testid="timeline-groups">
                {visibleTimelineGroups.map((group) => {
                  const displayedEvents = customerTimelineEvents(group.events, group.code);
                  return (
                    <details className={`timeline-group ${group.state} ${group.tone}`} key={group.code}>
                      <summary>
                        <span className="timeline-group-icon"><FlowIcon name={group.icon} size={17}/></span>
                        <span className="timeline-group-title">
                          <strong>{group.label}</strong>
                          <small className="timeline-group-description">{group.description}</small>
                          {displayedEvents.length > 0 && (
                            <small className="timeline-group-count">{`${displayedEvents.length} scan${displayedEvents.length === 1 ? '' : 's'}`}</small>
                          )}
                        </span>
                        <time dateTime={group.latestEvent?.occurredAt || undefined}>{group.latestEvent
                          ? formatDate(group.latestEvent.occurredAt)
                          : group.state === 'current'
                            ? formatDate(currentStatus?.occurredAt)
                            : group.state === 'done' ? 'Completed' : 'Upcoming'}</time>
                        <span className="timeline-chevron" aria-hidden="true">⌄</span>
                      </summary>
                      <div className="timeline-group-events">
                        {displayedEvents.length ? displayedEvents.map((event, index) => {
                          const eventLocation = formatLocation(event.location);
                          const isChildStatus = eventChildStatus(event, group.code);
                          const scanText = formatCarrierScan(event.rawScan)
                            || (isChildStatus
                              ? (formatCarrierScan(event.name) || formatCarrierScan(event.status?.name))
                              : null)
                            || formatCarrierScan(event.description)
                            || formatCarrierScan(event.name);
                          const eventName = scanText || `${group.label} update received`;
                          const formattedDescription = scanText && event.description
                            ? formatCarrierScan(event.description)
                            : null;
                          const eventDescription = formattedDescription && formattedDescription.toLowerCase() !== String(eventName).toLowerCase()
                            ? formattedDescription
                            : null;
                          const eventState = timelineClass(event);
                          return (
                            <div className={`timeline-scan ${eventState}`} key={event.id || `${event.occurredAt}-${index}`}>
                              <span className="timeline-dot">{eventState === 'failed' ? '!' : '✓'}</span>
                              <div>
                                <strong>{eventName}</strong>
                                {eventDescription && <p>{eventDescription}</p>}
                                {eventLocation && <small className="timeline-location"><FlowIcon name="pin" size={12}/>{eventLocation}</small>}
                              </div>
                              <time dateTime={event.occurredAt || undefined}>{formatDate(event.occurredAt)}</time>
                            </div>
                          );
                        }) : null}
                      </div>
                    </details>   
                );})}
              </div>
              {!visibleTimelineGroups.length && <p className="flow-empty-state timeline-empty">No completed or current tracking event is available yet.</p>}
            </section>

            {shareUrl && (
              <section className="flow-card timeline-share">
                <div><h3>Share Tracking</h3><p>Copy the customer tracking link</p></div>
                <div>
                  <div className="share-link"><span>{shareUrl}</span><button type="button" aria-label="Copy tracking link" onClick={handleCopy}><FlowIcon name="copy"/></button></div>
                  {copyMessage && <small className="copy-feedback" role="status">{copyMessage}</small>}
                </div>
              </section>
            )}
          </div>

          <div className="col-12 col-xl-3 side-column">
            <section className="flow-card info-card">
              <h2><FlowIcon name="user"/>Customer Information</h2>
              <div className="info-line"><FlowIcon name="user"/><p><span>Name</span><b>{customerName}</b></p></div>
              <div className="info-line"><FlowIcon name="mail"/><p><span>Email</span><b>{maskEmail(customer.email)}</b></p></div>
              <div className="info-line"><FlowIcon name="phone"/><p><span>Mobile</span><b>{maskPhone(customer.phone || deliveryAddress.phone)}</b></p></div>
            </section>

            <section className="flow-card info-card delv-address">
              <h2 className={`orange ${isFailed ? 'status-failed' : isDelivered ? 'status-success' : isException ? 'status-return' : ''}`}><FlowIcon name="pin"/>Delivery Address <small>{statusName}</small></h2>
              <address>
                <strong>{fullName(deliveryAddress, customer)}</strong>
                {addressLines.length
                  ? addressLines.map((line, index) => <span key={`${line}-${index}`}>{line}</span>)
                  : <span>Not available</span>}
                {deliveryAddress.phone && (<span>{maskPhone(deliveryAddress.phone)}</span>)}              
              </address>
            </section>

            <section className="flow-card info-card help-card">
              <h2>Share Shipment</h2><p>Send these tracking details to anyone</p>
              <div className="help-actions">
                <a className="whatsapp" href={whatsappShareUrl} target="_blank" rel="noopener noreferrer" aria-label="Share tracking details on WhatsApp"><FlowIcon name="whatsapp"/>WhatsApp</a>
                <a className="sms" href={smsShareUrl} aria-label="Share tracking details by SMS"><FlowIcon name="chat"/>SMS</a>
                <a className="email" href={emailShareUrl} aria-label="Share tracking details by email"><FlowIcon name="mail"/>Email</a>
              </div>
            </section>
          </div>
        </div>
      </main>

      <footer className="flow-footer"><p>© 2026 Shipra Logistics. All rights reserved.</p><nav><a href="#privacy">Privacy Policy</a><i/><a href="#terms">Terms &amp; Conditions</a></nav></footer>
    </div>
  );
}

export default TrackingFlow;
